import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { InputFieldType } from '../../models/input-types.enum';
import { DBEntity } from '../../models/db-entity.model';
import { DealerFormComponent } from '../../../modules/clients/components/dealer-form/dealer-form.component';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { DialogService } from 'primeng/dynamicdialog';
import { DateRangeSelectorComponent } from '../date-range-selector/date-range-selector.component';

export enum DateRangeOptionName {
  AllTime = 'AllTime',
  PastSevenDays = 'Past7days',
  ThisMonth = 'ThisMonth',
  ThisYear = 'ThisYear',
  PastYear = 'PastYear',
  CustomRange = 'CustomRange'
}

export const DATE_RANGE_OPTIONS = [
  { id: DateRangeOptionName.AllTime, name: 'Inception to date' },
  { id: DateRangeOptionName.PastSevenDays, name: 'Past 7 Days' },
  { id: DateRangeOptionName.ThisMonth, name: 'This Month' },
  { id: DateRangeOptionName.ThisYear, name: 'This Year' },
  { id: DateRangeOptionName.PastYear, name: 'Past Year' },
  { id: DateRangeOptionName.CustomRange, name: 'Custom Range' },
];

@UntilDestroy()
@Component({
  selector: 'app-date-range-options',
  templateUrl: './date-range-options.component.html',
  styleUrls: ['./date-range-options.component.scss'],
  providers: [DialogService]

})
export class DateRangeOptionsComponent implements OnInit {
  @Input() form!: FormGroup;
  @Input() controlName!: string;
  @Input() placeholder = '';
  @Input() label = 'Date Range';
  @Input() options: { id: DateRangeOptionName, name: string }[] = DATE_RANGE_OPTIONS;
  @Input() optionLabel: string = 'name';
  @Input() optionValue: string = 'id';
  @Input() autoDisplayFirst = false;
  @Input() disabled = false;
  @Output() allTimeSelected = new EventEmitter<boolean>();
  @Input() set setRangeToEdit(range: Date[] | string | undefined) {
    if (!range) {
      return;
    }

    if (range === 'all_time') {
      this.selectedOption = DateRangeOptionName.AllTime;
      this.rangeDates = undefined;
      this.form.get(this.controlName)?.setValue('all_time');
      this.allTimeSelected.emit(true);
    } else if (Array.isArray(range)) {
      this.selectedOption = DateRangeOptionName.CustomRange;
      this.rangeDates = range;
    }
  }

  selectedOption?: DateRangeOptionName;
  rangeDates: Date[] | undefined;

  constructor(
    public dialogService: DialogService,
  ) { }

  ngOnInit(): void {


  }

  protected readonly Validators = Validators;
  protected readonly InputFieldType = InputFieldType;

  onSelectedOptionChange() {
    if (this.disabled) {
      return;
    }

    if (this.selectedOption === DateRangeOptionName.AllTime) {
      // Handle Inception to date selection
      this.rangeDates = undefined;
      this.form.get(this.controlName)?.setValue('all_time');
      this.allTimeSelected.emit(true);
    } else if (this.selectedOption === DateRangeOptionName.CustomRange) {
      this.openCustomRangeDialog();
    } else {
      this.allTimeSelected.emit(false);
      this.initRangeDates();
    }
  }

  private initRangeDates() {
    const today = new Date();
    const todayUTC = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
    let startDate: Date = new Date();
    let endDate: Date = new Date();

    switch (this.selectedOption) {
      case DateRangeOptionName.PastSevenDays:
        startDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate() - 7));
        endDate = todayUTC;
        break;
      case DateRangeOptionName.ThisMonth:
        startDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
        endDate = todayUTC;
        break;
      case DateRangeOptionName.ThisYear:
        startDate = new Date(Date.UTC(today.getUTCFullYear(), 0, 1));
        endDate = todayUTC;
        break;
      case DateRangeOptionName.PastYear:
        startDate = new Date(Date.UTC(today.getUTCFullYear() - 1, today.getUTCMonth(), today.getUTCDate()));
        endDate = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
        break;
      case DateRangeOptionName.CustomRange:
        // Custom range will be handled by the dialog
        return;
      default:
        this.rangeDates = undefined;
        return;
    }
    this.setRangeDates(startDate, endDate);
  }

  setRangeDates(startDate: Date, endDate: Date) {
    this.rangeDates = [startDate, endDate];
    this.form.get(this.controlName)?.setValue(this.rangeDates);
  }

  openCustomRangeDialog(): void {
    const dialogRef = this.dialogService.open(DateRangeSelectorComponent, {
      header: 'Select Date Range',
      width: '500px',
      closable: false,
      data: {
        rangeDates: this.rangeDates
      }
    });

    dialogRef.onClose.pipe(untilDestroyed(this))
      .subscribe(res => {
        if (res) {
          this.setRangeDates(res.startDate, res.endDate);
        } else {
          // User cancelled the dialog, reset the dropdown selection
          this.selectedOption = undefined;
        }
      })
  }
}
